import React, { useCallback, useState } from "react";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Select,
  OutlinedInput,
  Chip,
  Box,
  InputLabel,
  FormControl,
  TextField,
  ListItemIcon,
  CircularProgress,
  IconButton,
  Grid,
  Divider,
  Tooltip,
  Toolbar,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useQuery } from "react-query";
import { getVehicleGroups } from "../../services/vehicle";
import VehicleGroupDetails from "./VehicleGroupDetails";
import Check from "../../auth/ability";
import { CAN, SUBJECTS } from "../../auth/permissions";
import { useEffect } from "react";
import { getUserDetails } from "../../services/Users";
import CreateVehicleGroup from "./group/CreateVehicleGroup";
import { DeleteOutline } from "@mui/icons-material";
import DeleteGroupConfirmDialog from "./group/DeleteGroupConfirmDialog";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const ALL = { vehicleGroupName: "All" };

const VehicleGroupList = ({
  selectedGroupsItems,
  setSelectedGroupsItems,
  doRefetchGroupsList,
  refetchGroupsList,
  vehicleId,
  setVehicleId,
  openQueryBuilder,
  extraFilters,
  setExtraFilters,
}) => {
  const [openStaticGroupCreateDialog, setOpenStaticGroupCreateDialog] =
    useState(false);
  const [selectedGroups, setSelectedGroups] = useState([ALL]);
  const [open, setOpen] = useState(false);
  const [queryGroupName, setQueryGroupName] = useState("");
  const [queryGroupDisplayName, setQueryGroupDisplayName] = useState("");
  const [userId, setUserId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const openGroupDetails = (name) => {
    if (name) {
      setQueryGroupName(name);
    }
    setOpen(true);
  };

  const saveFilterItemsToFilterArray = (item) => {
    const { columnField, operatorValue, value } = item;
    const oldItems = extraFilters;
    const newItems = oldItems.filter((i) => i.columnField !== columnField);
    setExtraFilters([...newItems, item]);
  };

  const removeFilterItemsFromFilterArray = (columnField) => {
    const vehicleKeyContainsString = extraFilters.filter((i) => {
      return i.columnField === "vehicleKey";
    });
    if (!!vehicleKeyContainsString?.[0]?.value?.searchString) {
      delete vehicleKeyContainsString[0].value.items;
      const newItems = extraFilters.filter(
        (i) => i.columnField !== columnField
      );
      setExtraFilters([...newItems, ...vehicleKeyContainsString]);
    } else {
      const newItems = extraFilters.filter(
        (i) => i.columnField !== columnField
      );
      setExtraFilters([...newItems]);
    }
  };

  const generateSearchQueryObject = (vehicleKeys) => {
    const vehicleKeyContainsString = extraFilters.filter((i) => {
      return i.columnField === "vehicleKey";
    });
    if (vehicleKeys && vehicleKeys?.length) {
      if (!!vehicleKeyContainsString?.[0]?.value?.searchString) {
        saveFilterItemsToFilterArray({
          value: {
            items: vehicleKeys,
            searchString: vehicleKeyContainsString?.[0]?.value?.searchString,
          },
          operatorValue: "inAndLike",
          columnField: "vehicleKey",
        });
      } else {
        saveFilterItemsToFilterArray({
          value: { items: vehicleKeys },
          operatorValue: "inAndLike",
          columnField: "vehicleKey",
        });
      }
    } else {
      removeFilterItemsFromFilterArray("vehicleKey");
    }
  };

  const handleClose = () => {
    setOpen(false);
    setQueryGroupName("");
    setTimeout(() => {
      if (doRefetchGroupsList) doRefetchGroupsList();
    }, 1000);
  };

  // Queries
  const {
    data: vehicleGroups = [],
    isLoading,
    refetch,
  } = useQuery("vehicleGroup", getVehicleGroups, {
    refetchOnWindowFocus: false,
  });

  const handleChange = (item) => {
    let result;
    if (item) {
      if (item && item.vehicleGroupName === "All") {
        // setSelectedGroups([ALL]);
        result = [ALL];
        setSelectedGroups(result);
        setSelectedGroupsItems([]);
      } else {
        const isItemAlreadySelected = selectedGroups.some(
          (i) => i.vehicleGroupName === item.vehicleGroupName
        );
        if (isItemAlreadySelected) {
          const newGroups = selectedGroups.filter(
            (i) => i.vehicleGroupName !== item.vehicleGroupName
          );
          if (newGroups.length) {
            // setSelectedGroups([...newGroups]);
            result = [...newGroups];
          } else {
            // setSelectedGroups([ALL]);
            result = [ALL];
          }
        } else {
          const newGroups = [
            ...selectedGroups.filter(
              (i) => i.vehicleGroupName !== ALL.vehicleGroupName
            ),
            item,
          ];
          result = [...newGroups];
        }
      }
      setSelectedGroups(result);
      const groups = result?.filter(
        (i) => i.vehicleGroupName !== ALL.vehicleGroupName
      );
      const vehicles = groups?.map((i) => i.vehicles.map((v) => v?.vehicleKey));
      const vehicleKeys = vehicles.flat();
      generateSearchQueryObject(vehicleKeys);
    }
  };

  function getStyles(item) {
    const isItemInSelectedArray = selectedGroups.some(
      (i) => i.vehicleGroupName === item
    );
    return {
      height: "48px",
      fontWeight: !isItemInSelectedArray ? "regular" : "medium",
      backgroundColor: !isItemInSelectedArray ? "" : "rgba(239, 108, 0, 0.08)",
    };
  }

  useEffect(() => {
    if (refetchGroupsList) {
      if (refetch) refetch();
    }
  }, [refetchGroupsList, refetch]);

  useEffect(() => {
    let fn = async () => {
      let { userName: name, userId: id } = await getUserDetails();
      setUserId(id.split("-").join(""));
    };
    fn();
    return () => { };
  }, []);

  const groupDetailsDialogProps = {
    openGroupDetails,
    handleClose,
    open,
    doRefetchGroupsList,
    refetchGroupsList,
    queryGroupName,
    setQueryGroupName,
  };

  const groupDetailsProps = {
    openGroupDetails,
    onClose: handleClose,
    open,
    doRefetchGroupsList,
    refetchGroupsList,
    groupObj: queryGroupName,
    name: queryGroupName,
    setQueryGroupName,
    displayName: queryGroupDisplayName,
  };

  const getGroupType = (typeString) => {
    let chipText = "";
    let chipColor = "default";
    if (typeString === "sys" || typeString === "dtg-sys") {
      chipText = "SYSTEM";
      chipColor = "success";
    } else {
      chipText = "USER";
      chipColor = "primary";
    }

    return (
      <Chip
        style={{ transform: "scale(0.70)" }}
        label={chipText}
        color={chipColor}
        size="small"
        variant="outlined"
      />
    );
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" component="div">
          Vehicle Groups
        </Typography>
        <div
          className="groups-container"
          style={{
            height: "calc(100vh - 330px)",
            display: "grid",
            gridTemplateRows: "min-content auto",
          }}
        >
          <FormControl sx={{ m: 1, width: "90%", height: "auto" }}>
            <InputLabel id="demo-multiple-chip-label">Filter</InputLabel>
            <Select
              labelId="multiple-chip-label"
              id="multiple-chip"
              style={{ maxHeight: "320px", overflowY: "auto" }}
              multiple
              value={selectedGroups}
              onChange={handleChange}
              open={false}
              input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
              IconComponent={() => <></>}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip
                      key={value.vehicleGroupName}
                      label={value.vehicleGroupName}
                      onDelete={
                        value.vehicleGroupName !== "All"
                          ? () => handleChange(value)
                          : false
                      }
                    />
                  ))}
                </Box>
              )}
              renderinput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  label="Multiple values"
                  placeholder="Favorites"
                />
              )}
              MenuProps={MenuProps}
            ></Select>
          </FormControl>
          <List className="vehicle-list-wrapper" style={{ height: "100%" }}>
            {isLoading ? (
              <Box
                sx={{ display: "flex" }}
                style={{
                  height: "100%",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                <ListItemButton
                  disablePadding
                  key={0}
                  onClick={() => handleChange(ALL)}
                  // style={getStyles(ALL.vehicleGroupName)}
                  selected={selectedGroups.some(
                    (i) => i.vehicleGroupName === ALL.vehicleGroupName
                  )}
                >
                  <ListItemButton>
                    <ListItemText primary={ALL.vehicleGroupName} />
                  </ListItemButton>
                </ListItemButton>
                {vehicleGroups?.length
                  ? vehicleGroups.map((vehicleGroup) => {
                    return (
                      <Box key={vehicleGroup.vehicleGroupId}>
                        <Divider />
                        <ListItemButton
                          disablePadding
                          key={vehicleGroup.vehicleGroupId}
                          // style={getStyles(vehicleGroup.vehicleGroupName)}
                          selected={selectedGroups.some(
                            (i) =>
                              i.vehicleGroupName ===
                              vehicleGroup.vehicleGroupName
                          )}
                        >
                          <Tooltip title={vehicleGroup.vehicleGroupName}>
                            <ListItemButton
                              onClick={() => handleChange(vehicleGroup)}
                              style={{
                                display: "block",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                paddingRight: "78px",
                                paddingLeft: "16px",
                              }}
                            >
                              {/* <ListItemIcon>
                              {getGroupType(vehicleGroup.groupType)}
                            </ListItemIcon> */}
                              {vehicleGroup.vehicleGroupName}
                            </ListItemButton>
                          </Tooltip>
                          <Check I={CAN.MANAGE} a={SUBJECTS.GROUP}>
                            <Tooltip
                              title={`Edit group ${vehicleGroup.vehicleGroupName}`}
                            >
                              <IconButton
                                edge="end"
                                aria-label="edit"
                                style={{ position: "absolute", right: "12px" }}
                              >
                                <EditIcon
                                  onClick={() => {
                                    setOpenStaticGroupCreateDialog(
                                      vehicleGroup
                                    );
                                    // openGroupDetails(vehicleGroup);
                                    setQueryGroupDisplayName(
                                      vehicleGroup.vehicleGroupName
                                    );
                                  }}
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title={`Delete group ${vehicleGroup.vehicleGroupName}`}
                            >
                              <IconButton
                                edge="end"
                                aria-label="edit"
                                style={{ position: "absolute", right: "48px" }}
                              >
                                <DeleteOutline
                                  onClick={() => {
                                    setOpenDeleteDialog(vehicleGroup);
                                  }}
                                />
                              </IconButton>
                            </Tooltip>
                          </Check>
                        </ListItemButton>
                      </Box>
                    );
                  })
                  : null}
              </>
            )}
          </List>
        </div>
      </CardContent>
      <Check I={CAN.CREATE} a={SUBJECTS.GROUP}>
        <Toolbar>
          <Grid container justifyContent={"flex-start"}>
            <Grid item>
              <Button onClick={() => setOpenStaticGroupCreateDialog(true)}>
                Create Group
              </Button>
            </Grid>
          </Grid>
        </Toolbar>
      </Check>
      <VehicleGroupDetails {...groupDetailsProps} />
      {openStaticGroupCreateDialog && (
        <CreateVehicleGroup
          open={openStaticGroupCreateDialog}
          toggleDialog={() =>
            setOpenStaticGroupCreateDialog(!openStaticGroupCreateDialog)
          }
        />
      )}
      {openDeleteDialog && (
        <DeleteGroupConfirmDialog
          id={openDeleteDialog?.vehicleGroupId}
          name={openDeleteDialog?.vehicleGroupName}
          type="vehicle group"
          toggleDialog={() => setOpenDeleteDialog(false)}
        />
      )}
    </Card>
  );
};

export default VehicleGroupList;
